import { ActionTree } from 'vuex';
import {
    TaskChecklistCreateRequestPayload,
    TaskChecklistDeleteRequestPayload,
    TaskChecklistItemMarkRequestPayload,
    TaskChecklistUpdateRequestPayload,
    TaskCommentCreateRequestPayload,
    TaskCommentDeleteRequestPayload,
    TaskCommentUpdateRequestPayload,
    TaskMediaCreateRequestPayload,
    TaskMediaDeleteRequestPayload,
    TaskMediaUpdateRequestPayload,
    TaskState,
    TaskStatusPayload,
} from './types';
import { RootState, SingleFilter } from '@/store/types';
import api from '@/api';
import * as taskRequests from '@/api/requests/work/task';
import { ChecklistRequest, MediaResource } from '@/api/requests/types';
import Task from '@/api/models/task';
import { TaskResource } from '@/api/requests/work/types';
import Event from '@/api/models/event';
import * as workRequests from '@/api/requests/work/work';

export const actions: ActionTree<TaskState, RootState> = {
    /**
     * Fetch all tasks.
     *
     * @param commit
     * @param getters 
     */
    async fetchTasks({ commit, dispatch, state, getters }, filters) {
        await commit('resetTasks');
        await commit('setFilters', filters);
        const response = await api.call(taskRequests.ALL(getters.httpQueryParams));
        commit('setTasks', response.data.data);
    },

    /**
     * Create a new task.
     *
     * @param commit
     * @param task
     */
    async createTask({ state, dispatch, commit, rootState }, task: Task) {
        const response = await api.call(taskRequests.STORE(task.toRequest()));
        const resource: TaskResource = response.data.data;

        await Promise.all(task.checklists.map((checklist) => {
            return api.call(taskRequests.ADD_CHECKLIST(resource.id, checklist.toRequest()));
        }));

        if(!!state.withEvent) {
            await dispatch('calendar/fetchAvailableCalendars', null, {root: true});

            // @ts-ignore
            let availableCalendars = rootState.calendar.availableCalendars;
            if(availableCalendars.length > 0) {
                let event = new Event({
                    start: task.start,
                    end: task.end,
                    summary: 'Costo ' + task.summary,
                    description: task.description,
                    taskId: resource.id,
                    calendarId: availableCalendars[0].id,
                    attendees: task.profileId ? [task.profileId] : [],
                    workId: task.workId
                });
                await dispatch('calendar/createEvent', event, {root: true});
            }
        }

        commit('addTask', resource);
    },

    /**
     * Delete task by id.
     *
     * @param commit
     * @param state
     * @param taskIds
     */
    async deleteTask({ commit, dispatch, state }, ...taskIds: number[]) {
        await Promise.all(taskIds.map(async (taskId) => {
            let currentTask = await api.call(taskRequests.FIND(taskId));
            let event = currentTask && currentTask.data ? currentTask.data.data.event : null;
            if(event != null) {
                await dispatch('calendar/fetchCalendars', null, {root: true});
                await dispatch('calendar/fetchEvents', {}, {root: true});
                await dispatch('calendar/deleteEvent', event.id, {root: true});
            }
            await api.call(taskRequests.DELETE(taskId));
            if (state.activeTaskId === taskId) {
                commit('resetActiveTaskId');
            }
            commit('removeTask', taskId);
        }));
    },

    /**
     * Update an existing task.
     *
     * @param commit
     * @param task
     */
    async updateTask({ state, dispatch, commit, rootState, rootGetters }, task: Task) {
        if (!task.id) {
            throw new Error('No task id provided.');
        }
        const response = await api.call(taskRequests.UPDATE(task.id, task.toRequest()));
        const resource: TaskResource = response.data.data;

        const taskId = task && task.id ? task.id : null;

        if(!!state.withEvent) {
            if(task.event != null) {

                task.event.start = task.start;
                task.event.end = task.end;
                task.event.attendees = task.profileId ? [task.profileId] : [];
                await dispatch('calendar/fetchCalendars', null, {root: true});
                await dispatch('calendar/fetchEvents', {}, {root: true});
                await dispatch('calendar/updateEvent', task.event, {root: true});

            } else {
                await dispatch('calendar/fetchAvailableCalendars', null, {root: true});
                // @ts-ignore
                let availableCalendars = rootState.calendar.availableCalendars
                if(availableCalendars.length > 0) {
                    let event = new Event({
                        start: task.start,
                        end: task.end,
                        summary: 'Costo ' + task.summary,
                        description: task.description,
                        taskId: resource.id,
                        calendarId: availableCalendars[0].id,
                        attendees: task.profileId ? [task.profileId] : [],
                        workId: task.workId
                    });
                    await dispatch('calendar/createEvent', event, {root: true});
                }
            }
        }

        await Promise.all(task.checklists.map((checklist) => {
            const taskId = parseInt(task && task.id ? task.id.toString() : '');
            const checklistId = parseInt(checklist && checklist.id ? checklist.id.toString() : '');
            if (checklistId) {
                return api.call(taskRequests.UPDATE_CHECKLIST(taskId, checklistId, checklist.toRequest()));
            } else {
                return api.call(taskRequests.ADD_CHECKLIST(taskId, checklist.toRequest()));
            }

        }));

        commit('upsertTask', resource);
    },

    /**
     * Reset a single filter.
     *
     * @param commit
     * @param dispatch
     * @param key
     */
    async resetFilter({ commit, dispatch }, key: string) {
        await commit('resetFilter', key);
        // fetch tasks
        await dispatch('fetchTasks');
    },

    /**
     * Set active task.
     *
     * @param commit
     * @param getters
     * @param taskId
     */
    async setActiveTask({ commit, getters }, taskId: number) {
        if (!getters.isTaskLoaded(taskId)) {
            const response = await api.call(taskRequests.FIND(taskId));
            commit('upsertTask', response.data.data);
        }
        commit('setActiveTaskId', taskId);
    },

    /**
     * Set filters.
     *
     * @param commit
     * @param dispatch
     * @param filters
     */
    setFilters({ commit, dispatch }, filters: SingleFilter[]) {
        filters.map((filter) => commit('setFilter', filter));
        // fetch tasks
        dispatch('fetchTasks');
    },

    /**
     * Set task status.
     *
     * @param commit
     * @param taskId
     * @param status
     */
    async setTaskStatus({ commit }, { taskId, status }: TaskStatusPayload) {
        const response = await api.call(taskRequests.CHANGE_STATUS(taskId, status));
        commit('updateTask', response.data.data);
    },

    /**
     * Mark task as completed.
     *
     * @param dispatch
     * @param taskId
     */
    async markTaskAsCompleted({ dispatch }, taskId: number) {
        dispatch('setTaskStatus', { taskId, status: 'completed' });
    },

    /**
     * Mark task as uncompleted.
     *
     * @param dispatch
     * @param taskId
     */
    async markTaskAsUncompleted({ dispatch }, taskId: number) {
        dispatch('setTaskStatus', { taskId, status: 'paused' });
    },

    /**
     * Start tracking of a task.
     *
     * @param getters
     * @param dispatch
     * @param taskId
     */
    async markTaskAsStarted({ getters, dispatch }, taskId: number) {
        const task = getters.getTaskById(taskId);
        if (task.status === 'completed') {
            console.error('Cannot track a completed task.');
            return;
        }
        dispatch('setTaskStatus', { taskId, status: 'started' });
    },

    /**
     * Pause tracking of a task.
     *
     * @param getters
     * @param dispatch
     * @param taskId
     */
    async markTaskAsPaused({ getters, dispatch }, taskId: number) {
        const task = getters.getTaskById(taskId);
        if (task.status === 'completed') {
            console.error('Cannot track a completed task.');
            return;
        }
        dispatch('setTaskStatus', { taskId, status: 'paused' });
    },

    /**
     * Add a checklist to a task.
     *
     * @param commit
     * @param taskId
     * @param checklist
     */
    async addChecklistToTask({ commit }, { taskId, checklist }: TaskChecklistCreateRequestPayload) {
        const response = await api.call(taskRequests.ADD_CHECKLIST(taskId, checklist));
        commit('addChecklistToTask', { taskId, checklist: response.data.data });
    },

    /**
     * Update a checklist into a task.
     *
     * @param commit
     * @param taskId
     * @param checklistId
     * @param checklist
     */
    async updateChecklistIntoTask({ commit }, { taskId, checklistId, checklist }: TaskChecklistUpdateRequestPayload) {
        const response = await api.call(taskRequests.UPDATE_CHECKLIST(taskId, checklistId, checklist));
        commit('addChecklistToTask', { taskId, checklist: response.data.data });
    },

    /**
     * Remove a checklist from a task.
     *
     * @param commit
     * @param taskId
     * @param checklistId
     */
    async removeChecklistFromTask({ commit }, { taskId, checklistId }: TaskChecklistDeleteRequestPayload) {
        const response = await api.call(taskRequests.REMOVE_CHECKLIST(taskId, checklistId));
        commit('removeChecklistFromTask', { taskId, checklist: response.data.data });
    },

    /**
     * Mark a checklist item as checked
     *
     * @param commit
     * @param taskId
     * @param checklistId
     * @param itemId
     */
    async markChecklistItemAsChecked({ commit }, { taskId, checklistId, itemId }: TaskChecklistItemMarkRequestPayload) {
        const response = await api.call(taskRequests.MARK_CHECKLIST_ITEM_AS_CHECKED(taskId, checklistId, itemId));
        commit('addChecklistItemToTask', { taskId, checklistId, item: response.data.data });
    },

    /**
     * Mark a checklist item as checked
     *
     * @param commit
     * @param taskId
     * @param checklistId
     * @param itemId
     */
    async markChecklistItemAsUnchecked({ commit }, {
        taskId,
        checklistId,
        itemId,
    }: TaskChecklistItemMarkRequestPayload) {
        const response = await api.call(taskRequests.MARK_CHECKLIST_ITEM_AS_UNCHECKED(taskId, checklistId, itemId));
        commit('addChecklistItemToTask', { taskId, checklistId, item: response.data.data });
    },

    /**
     * Add a comment to a task.
     *
     * @param commit
     * @param taskId
     * @param comment
     */
    async addCommentToTask({ commit }, { taskId, comment }: TaskCommentCreateRequestPayload) {
        const response = await api.call(taskRequests.ADD_COMMENT(taskId, comment));
        commit('addCommentToTask', { taskId, comment: response.data.data });
    },

    /**
     * Update a comment into a task.
     *
     * @param commit
     * @param taskId
     * @param commentId
     * @param comment
     */
    async updateCommentIntoTask({ commit }, { taskId, commentId, comment }: TaskCommentUpdateRequestPayload) {
        const response = await api.call(taskRequests.UPDATE_COMMENT(taskId, commentId, comment));
        commit('addCommentToTask', { taskId, comment: response.data.data });
    },

    /**
     * Remove a comment from a task.
     *
     * @param commit
     * @param taskId
     * @param commentId
     */
    async removeCommentFromTask({ commit }, { taskId, commentId }: TaskCommentDeleteRequestPayload) {
        await api.call(taskRequests.REMOVE_COMMENT(taskId, commentId));
        commit('removeCommentFromTask', { taskId, commentId });
    },

    /**
     * Add a media to a task.
     *
     * @param commit
     * @param taskId
     * @param file
     */
    async addMediaToTask({ commit }, { taskId, file }: TaskMediaCreateRequestPayload) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.call(taskRequests.ADD_MEDIA(taskId, formData, (progressEvent) => {
            // TODO: gestione progress upload.
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(percentCompleted);
        }));
        commit('addMediaToTask', { taskId, media: response.data.data });
    },

    /**
     * Update a media into a task.
     *
     * @param commit
     * @param taskId
     * @param mediaId
     * @param media
     */
    async updateMediaIntoTask({ commit }, { taskId, mediaId, media }: TaskMediaUpdateRequestPayload) {
        const response = await api.call(taskRequests.UPDATE_MEDIA(taskId, mediaId, media));
        commit('addMediaToTask', { taskId, media: response.data.data });
    },

    /**
     * Remove a media from a task.
     *
     * @param commit
     * @param taskId
     * @param mediaId
     */
    async removeMediaFromTask({ commit }, { taskId, mediaId }: TaskMediaDeleteRequestPayload) {
        await api.call(taskRequests.REMOVE_MEDIA(taskId, mediaId));
        commit('removeMediaFromTask', { taskId, mediaId });
    },

    /**
     * Download a media of a task.
     *
     * @param commit
     * @param getters
     * @param taskId
     * @param mediaId
     */
    async downloadMediaOfTask({ commit, getters }, { taskId, mediaId }: TaskMediaDeleteRequestPayload) {
        // Retrieve task
        const task = getters.getTaskById(taskId);

        // Retrieve media
        const media = task.media.find((item: MediaResource) => item.id === mediaId);

        if (!media) {
            throw new Error('No media to download');
        }

        const response = await api.call(taskRequests.DOWNLOAD_MEDIA(taskId, mediaId));
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', media.fileName);
        document.body.appendChild(link);
        link.click();
    },

    /**
     * Set with event status.
     *
     * @param commit
     * @param getters
     * @param taskId
     */
    setWithEventStatus({commit}, value : boolean) {
        commit('setWithEventStatus', value);
    }
};
